import React, { useId, useState } from 'react';
import { Button, Modal, message } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import SchoolSelect from '@/components/schoolselect';
import { updateSchool } from '@/apis/schools';
import { fetchMyProfile } from '@/apis/account';
import { setUserInfo } from '@/store/modules/user';
import usePrivateAction from '@/utils/usePrivateAction';
import { userRole } from '@/utils/roles';
import './index.scss';

const SchoolEditor = () => {
  const { isAuthenticated, userInfo, sessionVersion } = useSelector(state => state.user);
  const dispatch = useDispatch();
  const fieldId = useId();
  const [open, setOpen] = useState(false);
  const [school, setSchool] = useState(null);
  const [schoolName, setSchoolName] = useState('');
  const [pendingSchool, setPendingSchool] = useState(null);
  const [checked, setChecked] = useState(false);
  const { busy, run } = usePrivateAction();
  const locked = userInfo?.can_change_school === false;
  const nextChange = userInfo?.school_change_available_at;
  const nextDate = nextChange && Number.isFinite(Date.parse(nextChange)) ? new Date(nextChange).toLocaleString('zh-CN') : null;
  const sessionKey = `${isAuthenticated}:${userInfo?.id}:${sessionVersion}`;
  const confirmation = open && pendingSchool?.session === sessionKey ? pendingSchool : null;

  return <>
    <Button type="link" disabled={busy} onClick={() => {
      setSchool(userInfo?.school ?? null); setSchoolName(userInfo?.school_name || ''); setPendingSchool(null); setChecked(false); setOpen(true);
      run(fetchMyProfile, profile => {
        if (!profile || String(profile.id) !== String(userInfo?.id)) throw new Error('学校设置响应无效，请稍后重新打开');
        dispatch(setUserInfo(profile)); setSchool(profile.school ?? null); setSchoolName(profile.school_name || ''); setChecked(true);
      }, '学校设置加载失败，请稍后重新打开');
    }}>{userInfo?.school ? '修改学校' : '选择学校'}</Button>
    <Modal
      className="schoolEditor-modal"
      width={520}
      centered
      title={<div className="schoolEditor-heading">
        <span className="schoolEditor-icon" aria-hidden="true"><BankOutlined /></span>
        <div><span className="schoolEditor-title">选择学校</span><p>发现同校帖子，让推荐更贴近你的校园</p></div>
      </div>}
      open={open}
      okText="保存学校"
      cancelText="取消"
      confirmLoading={busy}
      okButtonProps={{ disabled: !school || busy || locked || !checked }}
      cancelButtonProps={{ disabled: busy }}
      closable={!busy}
      keyboard={!busy}
      mask={{ closable: !busy }}
      onCancel={() => { if (!busy) { setOpen(false); setPendingSchool(null); } }}
      onOk={() => {
        if (busy || locked || !checked || !school) return;
        if (String(school) === String(userInfo?.school)) { setOpen(false); return; }
        setPendingSchool({ school, name: schoolName || '所选学校', session: sessionKey });
      }}
    >
      <div className="schoolEditor-field">
        <label htmlFor={fieldId}>学校</label>
        <SchoolSelect id={fieldId} aria-label="学校" aria-describedby={`${fieldId}-help`} size="large" value={school} schoolName={schoolName} onChange={(value, option) => {
          setSchool(value); setSchoolName(typeof option?.label === 'string' ? option.label : ''); setPendingSchool(null);
        }} disabled={busy || locked || !checked} placeholder="输入学校名称搜索并选择" />
        <p id={`${fieldId}-help`} className="schoolEditor-help">{!checked ? (busy ? '正在核对学校修改资格…' : '学校设置未加载，请关闭后重试。') : locked ? `暂不可修改学校${nextDate ? `，下次可修改时间：${nextDate}` : '，请稍后重新查看'}。` : '输入名称关键词，从下拉列表中选择你的学校。'}</p>
      </div>
      <div className="schoolEditor-notes">
        <p>首页、推荐和附近招募将展示所选学校的帖子。</p>
        <p>切换学校不会移动原有帖子或改变聊天成员。</p>
      </div>
      <p className="schoolEditor-disclaimer">学校选择不代表学籍认证。</p>
    </Modal>
    <Modal title="确认学校选择" open={Boolean(confirmation)} centered width={420} okText="确认保存" cancelText="取消"
      confirmLoading={busy} okButtonProps={{ disabled: busy || locked || !checked }} cancelButtonProps={{ disabled: busy }}
      closable={!busy} keyboard={!busy} mask={{ closable: !busy }} onCancel={() => !busy && setPendingSchool(null)}
      onOk={() => {
        if (!confirmation || busy || locked || !checked) return;
        return run(() => updateSchool(confirmation.school), result => {
          dispatch(setUserInfo(result)); setPendingSchool(null); setOpen(false); message.success('学校已更新');
        });
      }}>
      <p>确认将学校设为「{confirmation?.name}」？</p>
      {userRole(userInfo) === 'user' && <p className="schoolEditor-change-limit" role="note">保存后需等待 365 天才能再次修改学校，请确认所选学校无误。</p>}
    </Modal>
  </>;
};
export default SchoolEditor;
